import { useMemo, useState } from 'react';
import { Badge, Button, Modal } from '../components/ui';

type Any = Record<string, any>;

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function FactCards({ block }: { block: Any }) {
  const items = Array.isArray(block.data?.items) ? block.data.items : [];
  return <div className="nb-grid nb-grid--2">{items.map((item: Any, index: number) => <div className="nb-item" key={index}>
    <div className="nb-item__title">{text(item.title ?? item.label)}</div>
    <div className="nb-stat-note">{text(item.text ?? item.description)}</div>
  </div>)}</div>;
}

function ComparisonTable({ block }: { block: Any }) {
  const columns = Array.isArray(block.data?.columns) ? block.data.columns : [];
  const rows = Array.isArray(block.data?.rows) ? block.data.rows : [];
  return <div className="table-wrap"><table className="table">
    <thead><tr>{columns.map((column: unknown, index: number) => <th key={index}>{text(column)}</th>)}</tr></thead>
    <tbody>{rows.map((row: unknown, index: number) => <tr key={index}>{Array.isArray(row) ? row.map((cell, cellIndex) => <td key={cellIndex}>{text(cell)}</td>) : <td>{text(row)}</td>}</tr>)}</tbody>
  </table></div>;
}

function QuizBlock({ block, index }: { block: Any; index: number }) {
  const [selected, setSelected] = useState<number | null>(null);
  const answers: string[] = Array.isArray(block.data?.answers) ? block.data.answers.map((answer: unknown) => String(answer)) : [];
  const correct = Number(block.data?.correctIndex);
  return <div className="nb-item">
    <div className="nb-item__title">{text(block.data?.question) || `Pytanie ${index + 1}`}</div>
    <div className="nb-list" style={{ marginTop: 8 }}>
      {answers.map((answer, answerIndex) => <Button key={answerIndex} size="sm" variant={selected === answerIndex ? (answerIndex === correct ? 'primary' : 'danger') : 'ghost'} onClick={() => setSelected(answerIndex)}>{answer}</Button>)}
    </div>
    {selected !== null ? <div className="nb-stat-note" style={{ marginTop: 8 }}>
      <strong>{selected === correct ? 'Poprawna odpowiedź.' : 'Niepoprawna odpowiedź.'}</strong> {text(block.data?.explanation)}
    </div> : null}
  </div>;
}

function ScenarioBlock({ block }: { block: Any }) {
  const [choice, setChoice] = useState<number | null>(null);
  const options = Array.isArray(block.data?.options) ? block.data.options : [];
  return <div className="nb-item">
    <div className="nb-item__title">{text(block.data?.question) || 'Wybierz wariant'}</div>
    <div className="nb-row" style={{ marginTop: 8 }}>{options.map((option: Any, index: number) => <Button key={index} size="sm" variant={choice === index ? 'primary' : 'ghost'} onClick={() => setChoice(index)}>{text(option.label)}</Button>)}</div>
    {choice !== null && options[choice] ? <div className="nb-stat-note" style={{ marginTop: 8 }}>{text(options[choice].advice ?? options[choice].text)}</div> : null}
  </div>;
}

function TimelineBlock({ block }: { block: Any }) {
  const items = Array.isArray(block.data?.items) ? block.data.items : [];
  return <ol className="nb-steps">{items.map((item: Any, index: number) => <li key={index}><strong>{text(item.label ?? item.title)}</strong>{text(item.text ?? item.description) ? <div className="nb-stat-note">{text(item.text ?? item.description)}</div> : null}</li>)}</ol>;
}

function GalleryBlock({ block }: { block: Any }) {
  const items = Array.isArray(block.data?.items) ? block.data.items : [];
  return <div className="nb-grid nb-grid--2">{items.map((item: Any, index: number) => <div className="nb-item" key={index}>
    <div className="nb-item__title">{text(item.label ?? item.title)}</div>
    <div className="nb-stat-note">{text(item.clue ?? item.text ?? item.description)}</div>
  </div>)}</div>;
}

function ChartBlock({ block }: { block: Any }) {
  const series = Array.isArray(block.data?.series) ? block.data.series : [];
  const values = series.flatMap((entry: Any) => Array.isArray(entry.points) ? entry.points.map(Number) : []).filter(Number.isFinite);
  const max = Math.max(1, ...values);
  return <div className="nb-list">{series.map((entry: Any, index: number) => <div className="nb-item" key={index}>
    <div className="nb-item__title">{text(entry.label)}</div>
    <div className="nb-row">{(entry.points ?? []).map((point: unknown, pointIndex: number) => <span key={pointIndex} className="nb-tag">{String(point)}</span>)}</div>
    <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden', marginTop: 8 }}><div style={{ height: '100%', width: `${Math.max(4, Math.min(100, (Number(entry.points?.[entry.points.length - 1]) / max) * 100))}%`, background: 'var(--nb-cyan)' }} /></div>
  </div>)}</div>;
}

function Block({ block, index }: { block: Any; index: number }) {
  switch (block.type) {
    case 'FactCards': return <FactCards block={block} />;
    case 'ComparisonTable': return <ComparisonTable block={block} />;
    case 'QuizBlock': return <QuizBlock block={block} index={index} />;
    case 'StepByStepScenario': return <ScenarioBlock block={block} />;
    case 'InteractiveTimeline': return <TimelineBlock block={block} />;
    case 'ObjectIdentificationGallery': return <GalleryBlock block={block} />;
    case 'DynamicChart': return <ChartBlock block={block} />;
    case 'SafetyNotice': return <div className="nb-danger-note">{text(block.data?.text)}</div>;
    case 'SourceReferences': {
      const sources = Array.isArray(block.data?.sources) ? block.data.sources : [];
      return <div className="nb-list">{sources.map((source: Any, sourceIndex: number) => <div key={sourceIndex}><a href={text(source.url)} target="_blank" rel="noreferrer">{text(source.name) || text(source.url)}</a>{source.credibility ? <span className="nb-item__meta">wiarygodność {source.credibility}/100</span> : null}</div>)}</div>;
    }
    case 'MapLayers': {
      const layers = Array.isArray(block.data?.layers) ? block.data.layers : [];
      return <div className="nb-item"><div className="nb-item__title">Warstwy mapy</div><div className="nb-row" style={{ marginTop: 8 }}>{layers.map((layer: unknown, layerIndex: number) => <span className="nb-tag" key={layerIndex}>{text(layer)}</span>)}</div><div className="nb-stat-note">Podgląd schematyczny. Rzeczywiste warstwy są dostępne w NieboGuard.</div></div>;
    }
    case 'EducationalCalculator': return <div className="nb-item"><div className="nb-item__title">Model edukacyjny</div><code>{text(block.data?.formula)}</code><div className="nb-stat-note">Zmienne: {Array.isArray(block.data?.variables) ? block.data.variables.join(', ') : '—'}. Wynik: {text(block.data?.outputLabel)}. To uproszczony model, nie narzędzie operacyjne.</div></div>;
    case 'LearningProgress': return <TimelineBlock block={{ data: { items: (block.data?.steps ?? []).map((step: unknown) => ({ label: step })) } }} />;
    default: return block.fallbackText ? <div className="nb-stat-note">{text(block.fallbackText)}</div> : <div className="nb-stat-note">Blok <code>{block.type}</code> nie ma jeszcze podglądu. Zostanie bezpiecznie pominięty.</div>;
  }
}

export function NieboGuardArticlePreview({ article, onClose }: { article: Any; onClose: () => void }) {
  const content = article?.content ?? {};
  const document = content.document ?? null;
  const blocks = Array.isArray(document?.blocks) ? document.blocks : [];
  const sources = Array.isArray(document?.sourceList) ? document.sourceList : Array.isArray(document?.sources) ? document.sources : [];
  const answers = useMemo(() => blocks.filter((block: Any) => block.type === 'QuizBlock').length, [blocks]);
  return <Modal open title={article?.title ?? 'Podgląd artykułu'} onClose={onClose} wide footer={<Button onClick={onClose}>Zamknij podgląd</Button>}>
    <div className="nb-list">
      <div className="nb-row">
        <Badge tone="info">{document?.format ?? 'SZKIC'}</Badge>
        <Badge tone={document?.safetyClassification === 'SAFETY_CRITICAL' ? 'danger' : 'neutral'}>{document?.safetyClassification ?? 'EVERGREEN'}</Badge>
        <Badge tone="warning">{article?.status ?? 'DRAFT'}</Badge>
        <span className="nb-item__meta">{answers} pytań quizowych · {blocks.length} bloków · {sources.length} źródeł</span>
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.55 }}>{content.lead}</p>
      {Array.isArray(content.sections) ? content.sections.map((section: Any, index: number) => <section key={index}>
        <h3>{section.heading}</h3>
        <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{section.content}</p>
      </section>) : null}
      {blocks.length ? <div className="nb-list">{blocks.map((block: Any, index: number) => <div key={index}><h4>{block.type === 'SourceReferences' ? 'Źródła' : `Blok ${index + 1}`}</h4><Block block={block} index={index} /></div>)}</div> : null}
      {Array.isArray(content.faq) && content.faq.length ? <section><h3>FAQ</h3>{content.faq.map((entry: Any, index: number) => <div key={index} className="nb-item"><div className="nb-item__title">{entry.question}</div><div className="nb-stat-note">{entry.answer}</div></div>)}</section> : null}
      {sources.length ? <section><h3>Źródła dokumentu</h3>{sources.map((source: Any, index: number) => <div key={index}><a href={text(source.url)} target="_blank" rel="noreferrer">{text(source.name) || text(source.url)}</a></div>)}</section> : null}
    </div>
  </Modal>;
}
